/**
 * Main unified hook for menu scope functionality
 * Combines all menu scope related logic into a single, easy-to-use hook
 *
 * @module hooks/menuScope/useMenuScope
 *
 * @param {Object} options - Configuration options for the hook
 * @param {boolean} [options.permissionOrganizationAccess] - Organization permission flag
 * @param {boolean} [options.autoRedirect=false] - Whether to auto-redirect to resume
 * @param {boolean} [options.trackEvents=false] - Whether to track analytics events
 * @param {string} [options.storeName] - Store name for content generation
 *
 * @returns {Object} Complete menu scope state and functionality
 * @returns {boolean} returns.isNewUser - Whether user is new (mainMenuOrganization flag AND store has no menus)
 * @returns {boolean} returns.isStoreMode - Whether in store mode
 * @returns {boolean} returns.isOrganizationMode - Whether in organization mode
 * @returns {string|null} returns.selectedScope - Currently selected scope
 * @returns {boolean} returns.hasOrganizationAccess - Whether has organization access (null treated as false)
 * @returns {boolean} returns.hasMenus - Whether the current store has menus
 * @returns {boolean} returns.isLoadingMenus - Whether menus are being loaded
 * @returns {number} returns.menusCount - Total number of menus in the store
 * @returns {boolean} returns.canAccessStore - Whether user can access Store mode
 * @returns {Object} returns.textKeys - Text keys for translations
 * @returns {string} returns.sucursalLabel - Label for store/branch card
 * @returns {Function} returns.setMenuScope - Function to set menu scope
 * @returns {Function} returns.clearMenuScope - Function to clear menu scope
 * @returns {Function} returns.handleScopeSelection - Function to handle scope selection with navigation
 *
 * @example
 * // Basic usage
 * const { isNewUser, textKeys, handleScopeSelection, canAccessStore } = useMenuScope();
 *
 * // With options
 * const menuScope = useMenuScope({
 *   permissionOrganizationAccess: true,
 *   autoRedirect: true,
 *   trackEvents: true,
 *   storeName: 'My Store'
 * });
 */

import { useLocation } from 'react-router-dom';
import useUserStore from 'hooks/useUserStore';
import { User } from 'containers/Users/types';
import { MENU_SCOPE } from 'utils/menuMaker/constants';

import { useMenuScopeUserState } from './useMenuScopeUserState';
import { useMenuScopeContent } from './useMenuScopeContent';
import { useMenuScopeEffects } from './useMenuScopeEffects';
import { useMenuScopeManagement } from './useMenuScopeManagement';
import { useMenuScopeNavigation } from './useMenuScopeNavigation';
import { useMenuScopeIdentifier } from './useMenuScopeIdentifier';

interface UseMenuScopeOptions {
  permissionOrganizationAccess?: boolean;
  autoRedirect?: boolean;
  trackEvents?: boolean;
  storeName?: string;
}

interface UseMenuScopeReturn {
  // User state
  isNewUser: boolean;
  isUserError: boolean;
  user: User | null;

  // Mode identification
  isStoreMode: boolean;
  isOrganizationMode: boolean;

  // Scope state
  selectedScope: string | null;
  hasOrganizationAccess: boolean;

  // Store menus state
  hasMenus: boolean;
  isLoadingMenus: boolean;
  menusCount: number;
  canAccessStore: boolean;

  // Content
  textKeys: ReturnType<typeof import('./useMenuScopeContent').useMenuScopeContent>['textKeys'];
  sucursalLabel: string;

  // Actions
  setMenuScope: (scope: MENU_SCOPE) => void;
  clearMenuScope: () => void;
  handleScopeSelection: (scope: MENU_SCOPE) => void;
}

export const useMenuScope = (options: UseMenuScopeOptions = {}): UseMenuScopeReturn => {
  const { permissionOrganizationAccess = false, autoRedirect = false, trackEvents = false, storeName } = options;

  const location = useLocation();
  const { storeState } = useUserStore();

  // 🧩 BUSINESS LOGIC: Separated into dedicated hooks

  // User state logic (new vs existing user)
  const {
    isNewUser,
    isUserError,
    user,
    hasMenus,
    isLoadingMenus,
    menusCount,
    canAccessStore,
  } = useMenuScopeUserState();

  // Mode identification
  const { isStoreMode, isOrganizationMode } = useMenuScopeIdentifier();

  // State management
  const {
    selectedScope,
    hasOrganizationAccess: hasOrgAccessRaw,
    setMenuScope,
    clearMenuScope,
  } = useMenuScopeManagement();

  // Convert null to false for backward compatibility
  const hasOrganizationAccess = hasOrgAccessRaw ?? false;

  // Navigation
  const { handleScopeSelection } = useMenuScopeNavigation({
    autoRedirectToResume: autoRedirect,
  });

  // Content generation (text keys and labels)
  const { textKeys, sucursalLabel } = useMenuScopeContent({
    isNewUser,
    hasOrganizationAccess: hasOrgAccessRaw,
    storeName: storeName || storeState.selected?.name,
  });

  // Side effects (permissions, navigation, analytics)
  useMenuScopeEffects({
    permissionOrganizationAccess: permissionOrganizationAccess !== undefined ? permissionOrganizationAccess : false,
    selectedScope,
    currentPath: location.pathname,
    trackEvents,
    storeDraft: storeState.storeDraft,
  });

  return {
    // User state
    isNewUser,
    isUserError,
    user,

    // Mode identification
    isStoreMode,
    isOrganizationMode,

    // Scope state
    selectedScope,
    hasOrganizationAccess,

    // Store menus state
    hasMenus,
    isLoadingMenus,
    menusCount,
    canAccessStore,

    // Content
    textKeys,
    sucursalLabel,

    // Actions
    setMenuScope,
    clearMenuScope,
    handleScopeSelection,
  };
};
